export function normalizePost(row) {
  if (!row) return null;
  const item = row.contentItem || row;
  const version = row.activeVersion || row.version || null;
  const platformVersions = (version && version.platformVersions) || {};
  const slides = Array.isArray(row.slides) ? row.slides : Array.isArray(row.carouselSlides) ? row.carouselSlides : [];
  const publishSummary = row.publishSummary || null;
  const publishMode = row.publishMode || row.generationBatch?.scheduleMode || 'draft';
  const generationStatus = item.generationStatus || (item.status === 'generating' ? 'generating' : null);
  const generationSettled = ['ready', 'failed'].includes(generationStatus);
  const isGenerating = !generationSettled && (item.status === 'generating' || Boolean(generationStatus));
  const format = item.format || row.generationBatch?.formats?.[0] || 'text_post';
  const publishScheduleAt = row.publishScheduleAt || publishSummary?.lastScheduledAt || row.generationBatch?.scheduleStartAt || null;
  const status = normalizeStatus({ item, generationStatus, isGenerating, publishSummary, publishMode, publishScheduleAt });
  const normalizedSlides = normalizeSlides(slides);
  const directMediaAssets = normalizeMediaAssets(row.mediaAssets || row.carouselMediaAssets || normalizedSlides.map(slide => slide.mediaAsset).filter(Boolean));
  const mediaAssets = directMediaAssets.some(asset => asset?.publicUrl)
    ? directMediaAssets
    : previewMediaAssets(row, version);
  // Text-only posts must never render a media tile. Backend used to seed
  // placeholder slides for every batch item regardless of format — frontend
  // would then show a black "Slide 1" placeholder for what should be a
  // copy-only post (Threads, X, LinkedIn). Drop slides with no real media
  // when format is text_post so MediaCarousel collapses to null.
  const hasRealMedia = normalizedSlides.some(slide => !slide.imageIsPlaceholder) || mediaAssets.some(asset => asset?.publicUrl);
  const slidesForRender = format === 'text_post' && !hasRealMedia ? [] : normalizedSlides;

  return {
    id: item.id,
    versionId: version?.id || null,
    topic: item.topic || item.title || '',
    title: item.title || version?.hook || item.topic || 'Generated post',
    format,
    status,
    publishSummary,
    publishMode,
    publishScheduleAt,
    generationStage: generationStatus,
    generationProgress: Number(item.generationProgress ?? (status === 'generating' ? 5 : 100)),
    hook: version?.hook || '',
    body: version?.body || '',
    caption: version?.caption || '',
    cta: version?.cta || '',
    hashtags: Array.isArray(version?.hashtags) ? version.hashtags : [],
    platformVersions,
    slides: slidesForRender.length ? slidesForRender : fallbackSlides(format, version, item, status),
    mediaAssets,
    raw: row,
  };
}

function normalizeStatus({ item, generationStatus, isGenerating, publishSummary, publishMode, publishScheduleAt }) {
  if (isGenerating) return 'generating';
  if (item.status === 'failed' || generationStatus === 'failed' || publishSummary?.failedCount > 0 || ['failed', 'partial_failure'].includes(publishSummary?.postStatus)) return 'failed';
  const targets = Array.isArray(publishSummary?.targets) ? publishSummary.targets : [];
  if (targets.some(target => targetIsPublishing(target, publishScheduleAt)) || publishSummary?.postStatus === 'queued') return 'publishing';
  if (isFutureTime(publishScheduleAt)) return 'scheduled';
  if (isPastDueTime(publishScheduleAt) && (item.status === 'scheduled' || publishSummary?.postStatus === 'scheduled' || publishSummary?.activeCount > 0 || publishMode !== 'draft')) return 'publishing';
  if (item.status === 'scheduled' || publishSummary?.postStatus === 'scheduled' || publishSummary?.activeCount > 0 || publishMode !== 'draft') return 'scheduled';
  if (targets.length > 0) return targets.every(target => target.status === 'published') ? 'published' : 'ready';
  if (publishSummary?.postStatus === 'published' || (!publishSummary && item.status === 'published')) return 'published';
  return 'ready';
}

function isFutureTime(value) {
  const timestamp = Date.parse(value || '');
  return Number.isFinite(timestamp) && timestamp > Date.now();
}

function isPastDueTime(value) {
  const timestamp = Date.parse(value || '');
  return Number.isFinite(timestamp) && timestamp <= Date.now();
}

function targetIsPublishing(target, fallbackScheduleAt) {
  if (['queued', 'publishing'].includes(target.status)) return true;
  if (target.status !== 'pending') return false;
  return !isFutureTime(target.scheduledAt || target.scheduled_at || fallbackScheduleAt);
}

function normalizeSlides(slides) {
  return slides.map((slide, index) => {
    const publicUrl = firstPublicUrl(slide.imageUrl, slide.publicUrl, slide.public_url, slide.mediaAsset?.publicUrl, slide.mediaAsset?.public_url);
    return {
      id: slide.id || index + 1,
      role: slide.role || slide.slideRole || null,
      label: slide.label || slideRoleLabel(slide.role || slide.slideRole, index),
      headline: slide.headline || slide.title || '',
      sub: slide.sub || slide.subtitle || slide.body || '',
      visualDirection: slide.visualDirection || slide.visual_direction || '',
      visualBrief: slide.visualBrief || slide.visual_brief || slide.visualDirection || slide.visual_direction || '',
      textOverlay: normalizeTextOverlay(slide.textOverlay || slide.text_overlay || slide.text_overlay_json),
      mediaAssetId: slide.mediaAssetId || slide.media_asset_id || slide.mediaAsset?.id || null,
      mediaAsset: normalizeMediaAsset(slide.mediaAsset || slide.media_asset || null),
      imageUrl: publicUrl || carouselPlaceholderUrl(index + 1),
      imageIsPlaceholder: !publicUrl,
      order: slide.order ?? slide.slideIndex ?? slide.slide_index ?? index,
    };
  });
}

function slideRoleLabel(role, index) {
  const labels = { hero: 'Hook', insight: 'Insight', infographic: 'Infographic', proof: 'Proof', brand: 'Brand', cta: 'CTA' };
  return labels[role] || (index === 0 ? 'Hook' : `Slide ${index + 1}`);
}

function normalizeTextOverlay(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      return normalizeTextOverlay(JSON.parse(value));
    } catch {
      return { text: value, source: 'post_copy' };
    }
  }
  if (typeof value !== 'object') return null;
  const text = typeof value.text === 'string' ? value.text.trim() : '';
  if (!text) return null;
  return { text, source: typeof value.source === 'string' ? value.source : 'post_copy' };
}

function fallbackSlides(format, version, item, status) {
  if (format !== 'carousel') return [];
  const hook = version?.hook || item.title || item.topic || 'Carousel hook';
  const body = version?.body || version?.caption || item.topic || 'Generated carousel';
  const includePlaceholderImages = status !== 'generating';
  return [
    { id: 'placeholder-1', label: 'Hook', headline: hook, sub: 'Slide 1', imageUrl: includePlaceholderImages ? carouselPlaceholderUrl(1) : null, imageIsPlaceholder: includePlaceholderImages, order: 0 },
    { id: 'placeholder-2', label: 'Takeaway', headline: body, sub: 'Slide 2', imageUrl: includePlaceholderImages ? carouselPlaceholderUrl(2) : null, imageIsPlaceholder: includePlaceholderImages, order: 1 },
  ];
}

function normalizeMediaAssets(value) {
  return Array.isArray(value) ? value.map(normalizeMediaAsset).filter(Boolean) : [];
}

function previewMediaAssets(row, version) {
  const urls = [];
  pushPublicUrl(urls, row?.mediaPreview?.url);
  pushPublicUrl(urls, row?.mediaPreview?.thumbnailUrl);
  pushPublicUrl(urls, version?.mediaPreview?.url);
  pushPublicUrl(urls, version?.mediaPreview?.thumbnailUrl);
  (row?.mediaUrls || row?.media_urls || []).forEach(value => pushPublicUrl(urls, value));
  (version?.mediaUrls || version?.media_urls || []).forEach(value => pushPublicUrl(urls, value));
  pushPublicUrl(urls, row?.imageUrl);
  pushPublicUrl(urls, version?.imageUrl);
  pushPublicUrl(urls, version?.thumbnailUrl);
  return urls.map((url, index) => ({ id: `preview-${index + 1}`, type: 'image', publicUrl: url }));
}

function pushPublicUrl(urls, value) {
  if (typeof value === 'string' && /^https?:\/\//.test(value) && !urls.includes(value)) urls.push(value);
}

function normalizeMediaAsset(asset) {
  if (!asset || typeof asset !== 'object') return null;
  return {
    id: asset.id || asset.mediaAssetId || null,
    type: asset.type || null,
    publicUrl: firstPublicUrl(asset.publicUrl, asset.public_url, asset.imageUrl, asset.url),
    r2Key: asset.r2Key || asset.r2_key || null,
    mimeType: asset.mimeType || asset.mime_type || null,
    width: numberOrNull(asset.width),
    height: numberOrNull(asset.height),
    // Video (reel) fields — null for images. durationSeconds → the duration pill;
    // posterUrl → the tile/player poster (resolvePosterUrl). poster_r2_key is NOT
    // surfaced (always NULL in v1 → no live reader; see resolvePosterUrl).
    durationSeconds: numberOrNull(asset.durationSeconds ?? asset.duration_seconds),
    posterUrl: firstPublicUrl(asset.posterUrl, asset.poster_url),
  };
}

// Reel duration → "m:ss" (e.g. 0:42, 1:30). Returns null for null/0 so callers
// omit the duration pill entirely rather than showing "0:00" (canonical spec §1).
export function formatDuration(seconds) {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return null;
  const m = Math.floor(total / 60);
  const s = Math.floor(total % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Single source of truth for "is this media a video (reel)?" — used by every
// surface (preview carousel, lightbox, edit post, pickers) so the detection
// never drifts. Accepts either the normalized (mimeType) or raw (mime_type) shape.
export function isVideoAsset(a) {
  return !!a && (a.type === 'video' || (a.mimeType || a.mime_type || '').startsWith('video/'));
}

// Reel poster resolver. v1 posters are NEVER stored (poster_r2_key is always
// NULL), so this returns an explicit public posterUrl when present, else null —
// callers then OMIT the poster attr so <video preload="metadata"> paints its own
// first decoded frame (never a broken-image glyph). The poster_r2_key→/media
// branch is intentionally absent: it has no live source in v1 (would be dead).
export function resolvePosterUrl(a) {
  return (a && (a.posterUrl || a.poster_url)) || null;
}

function firstPublicUrl(...values) {
  return values.find(value => typeof value === 'string' && /^https?:\/\//.test(value)) || null;
}

function numberOrNull(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function carouselPlaceholderUrl(index) {
  // Light gray + dark text reads as neutral "placeholder" on both light and
  // dark themes. The previous dark-navy (#111827) rendered as a near-black
  // square that looked like a broken image — particularly noticeable on
  // text-only posts (Threads, X, LinkedIn) where the placeholder showed up
  // for empty slide slots.
  const label = encodeURIComponent(`Slide ${index}`);
  return `https://placehold.co/1080x1350/E5E7EB/6B7280/png?text=${label}`;
}

export function postsFromBatchItems(items = []) {
  return items.map(normalizePost).filter(Boolean);
}
